import React, { useState } from "react";
import moment from "moment";
import { getRoomColor } from "../../utils/locationColor";
import "./AvailabilityCalendar.css";
import "./index.css";

const MONTHS = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
const WEEK_DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function getCalendar(firstMonthDayOfWeek, lastMonthDayOfWeek, daysInMonth) {
  let days = new Array(firstMonthDayOfWeek).fill("");

  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i.toString().padStart(2, "0"));
  }

  days = days.concat(new Array(6 - lastMonthDayOfWeek).fill(""));

  const calendar = [];
  let week = [];
  days.forEach((day, idx) => {
    week.push(day);
    if ((idx + 1) % 7 === 0) {
      calendar.push(week);
      week = [];
    }
  });

  return calendar;
}

const AvailabilityCalendar = ({
  availableDates = [],
  selectDate,
  month,
  setMonth,
  year,
  setYear,
  handleChangeDate,
  disableSelectTime = false,
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const daysInMonth = moment(`${year}${month}`, "YYYYMM").daysInMonth();
  const firstMonthDayOfWeek = moment(`${year}${month}01`, "YYYYMMDD").day();
  const lastMonthDayOfWeek = moment(`${year}${month}${daysInMonth}`, "YYYYMMDD").day();

  const calendar = getCalendar(firstMonthDayOfWeek, lastMonthDayOfWeek, daysInMonth);

  const handleOnSelectDate = (day) => {
    if (!day) return;
    const date = `${year}${month}${day}`;
    handleChangeDate(`${year}-${month}-${day}`);
    setSelectedDate(date);
    selectDate(date);
  };

  const getAvailableDayData = (day) => {
    const date = `${year}${month}${day}`;
    const availableDateInfo = availableDates.find(({ date: availableDate }) => date === availableDate);

    return availableDateInfo;
  };

  const generateStyleOfAvailableDate = (day) => {
    const availableDayData = getAvailableDayData(day);
    const selectedRooms = availableDayData?.selectedRooms ?? [];
    const colorableRooms = selectedRooms.filter((room) => Number.isInteger(room) && getRoomColor(room)?.color);

    if (colorableRooms.length < 1) {
      return {};
    }

    if (colorableRooms.length === 1) {
      return { backgroundColor: getRoomColor(colorableRooms[0]).color };
    }

    const segmentWidth = 100 / colorableRooms.length;
    const gradientStops = colorableRooms
      .map((room, index) => {
        const roomColor = getRoomColor(room).color;
        const start = (segmentWidth * index).toFixed(2);
        const end = (segmentWidth * (index + 1)).toFixed(2);
        return `${roomColor} ${start}% ${end}%`;
      })
      .join(" , ");

    return {
      backgroundImage: `linear-gradient(to right, ${gradientStops})`,
    };
  };

  const getHighlightClassName = (day) => {
    let className = "day-container";
    const date = `${year}${month}${day}`;
    const availableDayData = getAvailableDayData(day);
    if (!!day) {
      className = "hoverable";
      if (availableDayData) {
        const selectedRooms = (availableDayData.selectedRooms ?? []).filter(Number.isInteger);
        if (selectedRooms.length === 1) {
          const roomClassName = getRoomColor(selectedRooms[0])?.className;
          className += roomClassName ? ` ${roomClassName}` : " no-room-available-background";
        } else if (selectedRooms.length === 0) {
          className += " no-room-available-background";
        }
      }
      if (!availableDayData) {
        className += " unavailable";
      }
      if (selectedDate === date) {
        className += " selected";
      }
    }
    return className;
  };

  const goPrevMonth = () => {
    const idx = MONTHS.findIndex((m) => m === month);
    setMonth(idx === 0 ? MONTHS[11] : MONTHS[idx - 1]);
    setYear(idx === 0 ? (Number(year) - 1).toString() : year);
  };

  const goNextMonth = () => {
    const idx = MONTHS.findIndex((m) => m === month);
    setMonth(idx === 11 ? MONTHS[0] : MONTHS[idx + 1]);
    setYear(idx === 11 ? (Number(year) + 1).toString() : year);
  };

  return (
    <table className="available-calendar-table">
      <thead>
        <tr>
          <th onClick={goPrevMonth} className="calendar-nav-left-btn">
            &lt;
          </th>
          <th colSpan={5}>{moment(`${year}${month}`, "YYYYMM").format("MMM YYYY")}</th>
          <th onClick={goNextMonth} className="calendar-nav-right-btn">
            &gt;
          </th>
        </tr>
        <tr>
          {WEEK_DAYS.map((weekday) => (
            <th key={weekday}>{weekday}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {calendar.map((week, widx) => (
          <tr key={widx}>
            {week.map((day, didx) => (
              <td
                key={`${year}${month}${didx}`}
                className={getHighlightClassName(day)}
                style={generateStyleOfAvailableDate(day)}
                onClick={() => {
                  !disableSelectTime && handleOnSelectDate(day);
                }}
              >
                {!!day ? day : ""}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default AvailabilityCalendar;
